/**
 * Image Placeholder Section Component
 * Story: BLOG-AI-007 - Image Handling & Markdown Converter Enhancement
 *
 * Displays AI-generated image suggestions with placeholder replacement UI.
 * Shows image type, alt text, caption, and upload functionality.
 */
import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ImageIcon, Upload, CheckCircle, AlertCircle, Image as ImageIconLucide } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ImageSuggestion } from '@/types/blog.types'

interface ImagePlaceholderSectionProps {
  imageSuggestions: ImageSuggestion[]
  onImageUpload?: (position: number, file: File) => Promise<string>
  onAltTextChange?: (position: number, newAltText: string) => void
  disabled?: boolean
}

const IMAGE_TYPE_ICONS: Record<ImageSuggestion['type'], { icon: typeof ImageIconLucide; label: string; color: string }> = {
  chart: {
    icon: ImageIconLucide,
    label: 'Chart/Graph',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
  },
  diagram: {
    icon: ImageIconLucide,
    label: 'Diagram',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400',
  },
  photo: {
    icon: ImageIconLucide,
    label: 'Photo',
    color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400',
  },
  infographic: {
    icon: ImageIconLucide,
    label: 'Infographic',
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400',
  },
  screenshot: {
    icon: ImageIconLucide,
    label: 'Screenshot',
    color: 'bg-pink-100 text-pink-700 dark:bg-pink-950/30 dark:text-pink-400',
  },
}

export function ImagePlaceholderSection({
  imageSuggestions,
  onImageUpload,
  onAltTextChange,
  disabled = false,
}: ImagePlaceholderSectionProps) {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)
  const [uploadedUrls, setUploadedUrls] = useState<Record<number, string>>({})
  const [editedAltText, setEditedAltText] = useState<Record<number, string>>({})

  const handleFileSelect = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !onImageUpload) return

    setUploadingIndex(index)
    try {
      const url = await onImageUpload(index, file)
      setUploadedUrls(prev => ({ ...prev, [index]: url }))
    } catch (error) {
      console.error('Image upload failed:', error)
    } finally {
      setUploadingIndex(null)
    }
  }

  if (!imageSuggestions || imageSuggestions.length === 0) {
    return null
  }

  return (
    <Card className="bg-card text-card-foreground border-border p-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-primary" />
          <h4 className="font-semibold text-foreground">AI Image Suggestions</h4>
          <Badge variant="secondary" className="ml-auto">
            {imageSuggestions.length} {imageSuggestions.length === 1 ? 'image' : 'images'}
          </Badge>
        </div>

        <p className="text-sm text-muted-foreground">
          The AI identified strategic locations for images to enhance your content. Upload images to replace the
          placeholders.
        </p>

        {/* Image Suggestions List */}
        <div className="space-y-3">
          {imageSuggestions.map((suggestion, index) => {
            const typeConfig = IMAGE_TYPE_ICONS[suggestion.type]
            const Icon = typeConfig.icon
            const isUploaded = !!uploadedUrls[index] || !!suggestion.uploaded_url
            const isUploading = uploadingIndex === index

            return (
              <div
                key={`${suggestion.placeholder}-${index}`}
                className={cn(
                  'flex flex-col gap-3 p-3 rounded-lg border transition-colors',
                  isUploaded
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                    : 'bg-muted/50 border-border hover:border-primary/50'
                )}
              >
                {/* Header Row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Icon */}
                    <div className={cn('p-2 rounded-md flex-shrink-0', typeConfig.color)}>
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-foreground text-sm">Position {suggestion.position}</span>
                        <Badge variant="outline" className="text-xs">
                          {typeConfig.label}
                        </Badge>
                      </div>

                      {/* Editable Alt Text with Character Counter */}
                      <div className="mb-2">
                        <Label htmlFor={`alt-text-${index}`} className="text-xs text-muted-foreground mb-1 block">
                          Alt Text (SEO & Accessibility)
                          <span
                            className={cn(
                              'ml-2',
                              (editedAltText[index] || suggestion.alt_text).length > 125
                                ? 'text-destructive'
                                : 'text-muted-foreground'
                            )}
                          >
                            {(editedAltText[index] || suggestion.alt_text).length}/125
                          </span>
                        </Label>
                        <Textarea
                          id={`alt-text-${index}`}
                          value={editedAltText[index] ?? suggestion.alt_text}
                          onChange={(e) => {
                            const newAltText = e.target.value
                            setEditedAltText((prev) => ({ ...prev, [index]: newAltText }))
                            onAltTextChange?.(suggestion.position, newAltText)
                          }}
                          rows={2}
                          maxLength={125}
                          disabled={disabled}
                          className="text-sm bg-background border-border text-foreground resize-none"
                          placeholder="Descriptive text for screen readers and SEO"
                        />
                      </div>

                      {suggestion.caption && (
                        <p className="text-xs text-muted-foreground italic">&quot;{suggestion.caption}&quot;</p>
                      )}
                    </div>

                    {/* Status Icon */}
                    {isUploaded && (
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-500 flex-shrink-0" />
                    )}
                  </div>
                </div>

                {/* Upload Row */}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    {isUploaded ? (
                      <div className="flex items-center gap-2 p-2 bg-background rounded border border-border">
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-500" />
                        <span className="text-xs text-muted-foreground truncate">
                          {uploadedUrls[index] || suggestion.uploaded_url}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`image-upload-${index}`}
                          className="flex-1 flex items-center justify-center gap-2 p-2 border border-dashed border-border rounded cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors"
                        >
                          <Input
                            id={`image-upload-${index}`}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(index, e)}
                            disabled={disabled || isUploading}
                            className="hidden"
                          />
                          <Upload className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {isUploading ? 'Uploading...' : 'Upload image'}
                          </span>
                        </Label>

                        {onImageUpload === undefined && (
                          <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-500">
                            <AlertCircle className="h-4 w-4" />
                            <span>Upload handler not configured</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {isUploaded && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setUploadedUrls(prev => {
                        const next = { ...prev }
                        delete next[index]
                        return next
                      })}
                      disabled={disabled}
                      className="text-xs"
                    >
                      Replace
                    </Button>
                  )}
                </div>

                {/* Placeholder Tag */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
                  <code className="px-2 py-1 bg-muted rounded font-mono text-xs">{suggestion.placeholder}</code>
                  <span>will be replaced in content</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Help Text */}
        <div className="pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            💡 <strong>Tip:</strong> Upload images that match the AI&apos;s suggestions for optimal SEO and user
            experience. Images will automatically replace placeholders in your content.
          </p>
        </div>
      </div>
    </Card>
  )
}
